import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'

import {
  IconButton,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
  Box,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Popover,
  Divider,
} from '@mui/material'

import ImportExportIcon from '@mui/icons-material/ImportExport'
import { Settings as SettingsIcon, Delete as TrashIcon } from '@mui/icons-material/'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'

import { ACTIVITY_TYPES, FIELD_TYPES } from '../../../utility/constants'

import { useAuthContext } from '../../../hooks/useAuthContext'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { pageTemplateService } from '../../../services/pageTemplateService'
import { listDataService } from '../../../services/listDataService'
import { activityTemplateService } from '../../../services/activityTemplateService'
import { processTemplateService } from '../../../services/processTemplateService'
import EditFieldTemplateDialog from '../components/EditFieldTemplateDialog'
import UpdateActivityDetails from '../components/UpdateActivityDetails'
import { toast } from 'react-toastify'
import { workflowService } from '../../../services/admin/workflow'
import adminFieldService from '../../../services/admin/field'
import ConfirmationBox from '../../../components/Dashboard/ConfirmationBox'
import adminPageService from '../../../services/admin/page'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import OptionMenu from '../../../components/core-ui/OptionMenu'
import DerivePageDialog from '../components/DerivePageDialog'

import GroupsIcon from '@mui/icons-material/Groups'
import EditIcon from '@mui/icons-material/Edit'
import TableViewIcon from '@mui/icons-material/TableView'
import ImportPageDialog from '../components/ImportPageDialog'
import TabularFormDialog from '../components/TabularFormDialog'
import { getAxiosOptions } from '../../../services/serviceHelper'
import { makeStyles } from '@mui/styles'
import GridLayoutView from '../components/GridLayoutView'
import './Style.css'
import { adminTeamsService } from '../../../services/admin/teams'
import { copyToClipboard } from '../../../utility/clipboard'
import RuleEditorDialog from '../RuleEngine/RuleEditorDialog'

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
  },
}))

const iconButtonStyle = { backgroundColor: '#f2f2f2' }
const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const MOVING_INSTRUCTIONS_STATIC = {
  TOP: 'top',
  BOTTOM: 'bottom',
  UP: 'up',
  DOWN: 'down',
}

export default function ActivityConfig() {
  //state variables
  const [loading, setLoading] = useState(false)
  const [activityTemplateId, setActivityTemplateId] = useState(useParams().activity_template_id)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isEditPageDialogOpen, setIsEditPageDialogOpen] = useState(false)
  const [isEditActivityDialogOpen, setIsEditActivityDialogOpen] = useState(false)
  const [ftData, setFtData] = useState([])
  const [atData, setAtData] = useState(null)
  const [lists, setLists] = useState([])
  const [tfData, setTfData] = useState([])
  const [importedFieldList, setImportedFieldList] = useState([])
  const [dialogFtId, setDialogFtId] = useState(null)
  const [dialogPtId, setDialogPtId] = useState(null)
  const [editingPageName, setEditingPageName] = useState('')
  const [editingFieldName, setEditingFieldName] = useState('')
  const [pages, setPages] = useState([])
  const [showDeleteField, setShowDeleteField] = useState(null)
  const [showDeletePage, setShowDeletePage] = useState(null)
  const [deleteActivity, setDeleteActivity] = useState(null)
  const [derivePage, setDerivePage] = useState({ open: false, pageId: null })
  const [importField, setImportField] = useState({ open: false, pageId: null })
  const [tabularFormDialogOpen, setTabularFormDialogOpen] = useState(false)
  const [allActivities, setAllActivities] = useState([])
  const [gridLayoutOpen, setGridLayoutOpen] = useState(false)
  const [gridLayoutPageTemplateId, setGridLayoutPageTemplateId] = useState(null)

  const [allTeamTypes, setAllTeamTypes] = useState([])
  const [allTeamSubTypes, setAllTeamSubTypes] = useState([])
  const [openRuleSettingDialog, setOpenRuleSettingDialog] = useState(false)

  const updateFieldRef = useRef()

  const { user } = useAuthContext()
  const navigate = useNavigate()

  useEffect(() => {
    const ptIds = Array.from(
      new Set(ftData.sort((a, b) => a.page_num - b.page_num).map((ft) => ft.page_template_id)),
    )

    const pages = ptIds.map((it) => {
      const data = ftData.find((ft) => ft.page_template_id == it)
      return { id: it, page_template_name: data?.page_template_name, sequence: data?.page_num }
    })

    setPages(pages)
  }, [ftData])

  useEffect(() => {
    // call api to update field name
    if (!user) {
      return
    }
    ;(async () => {
      const props = {
        ftColumnValue: editingFieldName.value,
        fieldTemplateId: editingFieldName.id,
        ftColumnName: 'field_template_name',
      }
      await fieldTemplateService.updateFieldTemplate(user, props)

      // update local state to prevent api call for loading updated data every time
      setFtData(
        ftData.map((it) =>
          it.id == editingFieldName.id
            ? { ...it, field_template_name: editingFieldName.value }
            : it,
        ),
      )
    })()
  }, [editingFieldName, user])

  useEffect(() => {
    const field = ftData.find((it) => it.id === dialogFtId)
    setEditingFieldName({ id: dialogFtId, value: field?.field_template_name })
  }, [dialogFtId])

  async function handleUpdatePageTemplateDetails(dialogPtId, ptName) {
    const initialFtState = [...ftData]
    try {
      await axios.put(
        `${NODE_BASE_URL}/admin/template/page`,
        { page_template_id: dialogPtId, page_template_name: ptName.trim() },
        getAxiosOptions(user),
      )

      // update page details in field list
      const updatedFtData = ftData?.map((it) =>
        it.page_template_id == dialogPtId ? { ...it, page_template_name: ptName.trim() } : it,
      )
      setFtData(updatedFtData)

      setIsEditPageDialogOpen(false)
      setEditingPageName('')
    } catch (error) {
      // revert the action performed above
      setFtData(initialFtState)
    }
  }

  async function handleDeleteField(ftId) {
    const initialFtState = [...ftData]
    try {
      // delete the field from ftData
      await adminFieldService.deleteField(user, ftId)

      let updatedFtData = ftData?.filter((it) => it.id !== ftId)

      // delete table field as well if subfield is last one
      const ft = ftData.find((it) => it.id === ftId)
      if (ft.is_subfield) {
        const sfts = updatedFtData.filter(
          (it) => it.table_field_template_id === ft.table_field_template_id,
        )
        if (sfts.length < 1) {
          updatedFtData = updatedFtData.filter((it) => it.id != ft.table_field_template_id)
        }
      }

      setFtData(updatedFtData)
      setIsEditDialogOpen(false)
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message)
      // revert the action performed above
      setFtData(initialFtState)
    }
  }

  const handleDeletePage = async (pageTemplateId) => {
    const initialFtState = [...ftData]
    try {
      await adminPageService.deletePage(user, pageTemplateId)

      // delete the page from ftData
      const updatedFtData = ftData?.filter((it) => it.page_template_id != pageTemplateId)
      setFtData(updatedFtData)
      setIsEditDialogOpen(false)
      toast.success('Page deleted successfully.')
    } catch (error) {
      // revert the action performed above
      setFtData(initialFtState)
      toast.error('Something went wrong!')
    }
  }

  async function handleAddPage(activityTemplateId) {
    const initialState = [...ftData]
    try {
      const allFields = await pageTemplateService.addPageTemplate(user, activityTemplateId)
      setFtData([...ftData, ...allFields])
    } catch (error) {
      setFtData(initialState)
    }
  }

  async function handleAddField(pageTemplateId) {
    const initialFtData = [...ftData]
    try {
      const fieldsForPage = ftData.filter(
        (ft) => ft.page_template_id === pageTemplateId && ft.is_subfield !== true,
      )

      const sequenceArr = fieldsForPage.map((f) => f.sequence)
      const newSequenceNum = sequenceArr[sequenceArr.length - 1] + 1

      const newFieldDetails = {
        created_by: user.id,
        data_structure: 'text',
        field_template_name: 'New Field',
        is_required: true,
        modified_by: user.id,
        page_template_id: pageTemplateId,
        subfield_column_width_in_rem: 12,
        sequence: newSequenceNum,
      }

      const field = await fieldTemplateService.addFieldTemplate(user, newFieldDetails)
      setFtData([...ftData, field])
    } catch (error) {
      setFtData(initialFtData)
    }
  }

  async function handleAddSubfield(tableFieldTemplateId, pageTemplateId) {
    const initialFtData = [...ftData]
    try {
      const tableData = ftData.find((ft) => ft.id == tableFieldTemplateId)
      const subFieldArr = tableData.subfield_template_ids.split(',').map((sftId) => Number(sftId))
      const subfieldsForTable = ftData.filter((ft) => subFieldArr.includes(ft.id))
      const sequenceArr = subfieldsForTable.map((f) => f.sequence)
      const newSequenceNum = sequenceArr[sequenceArr.length - 1] + 1

      const newFieldDetails = {
        created_by: user.id,
        data_structure: 'text',
        field_template_name: 'New Subfield',
        is_required: true,
        modified_by: user.id,
        page_template_id: pageTemplateId,
        subfield_column_width_in_rem: 12,
        sequence: newSequenceNum,
        table_field_template_id: tableFieldTemplateId,
      }

      const subfield = await fieldTemplateService.addSubfieldTemplate(user, newFieldDetails)

      // get added fields
      const allFields = [...ftData, subfield]
      subFieldArr.push(subfield.id)

      // update table field subfields wala column
      const updatedFtData = allFields.map((it) =>
        it.id == tableFieldTemplateId
          ? { ...it, subfield_template_ids: subFieldArr.join(',') }
          : it,
      )
      setFtData(updatedFtData)
    } catch (error) {
      setFtData(initialFtData)
    }
  }

  const getDataFromServer = async () => {
    setLoading(true)
    // TODO:: Use promise.all to parallelize the api call to optimize initial loading
    const initialData = await Promise.all([
      fieldTemplateService.getActivityFieldsForAdmin(user, activityTemplateId),
      listDataService.getListOfListsForAdmin(user),
      activityTemplateService.getTemplate(user, activityTemplateId),
      fieldTemplateService.getImportedField(user),
      adminTeamsService.getTeamTypeAndSubTypes(user),
    ])

    const fieldTemplateData = initialData[0]
    const listOfLists = initialData[1]
    const atData = initialData[2].data
    const importedList = initialData[3]
    const tempList = await processTemplateService.getTemplateListForAdmin(
      user,
      atData?.process_template_id,
    )
    const teamTypeSubType = initialData[4]

    setImportedFieldList(
      importedList.map((it) => ({
        ...it,
        groupHeading: it.process_template_name + ' - ' + it.activity_template_name,
      })),
    )

    setFtData(fieldTemplateData)
    setLists(listOfLists)
    setAtData(atData)
    setTfData(tempList)
    setAllTeamTypes(teamTypeSubType?.teamTypes || [])
    setAllTeamSubTypes(teamTypeSubType?.teamSubTypes || [])
    setLoading(false)
  }

  useEffect(() => {
    ;(async () => {
      if (user && atData?.process_template_id) {
        const activities = await workflowService.getActivities(user, atData?.process_template_id)
        setAllActivities(activities.sort((a, b) => a.rank - b.rank))
      }
    })()
  }, [atData?.process_template_id, user])

  useEffect(() => {
    if (user) {
      getDataFromServer()
    }
  }, [user])

  const saveFieldTemplate = async (fieldTemplate, doInBackground) => {
    try {
      const field = ftData.find((it) => it.id == fieldTemplate.id)

      // check conditional requirement before saving
      // 1. for pivot field, source field is must
      if (fieldTemplate.is_pivot_field) {
        if (!fieldTemplate.import_from_process_template_id) {
          throw new Error('Source process is mandatory for pivot field.')
        }
      }

      // 2. for derived field, derived from field template is mandatory field
      if (fieldTemplate.is_derived_field) {
        if (!fieldTemplate.derived_from_field_template_id) {
          throw new Error('Derived from field is mandatory for derived field.')
        }
      }

      // 3. for imported field, pivot field && imported from field template is mandatory field
      if (fieldTemplate.is_imported_field) {
        if (
          !fieldTemplate.pivot_field_template_id ||
          !fieldTemplate.imported_from_field_template_id
        ) {
          throw new Error('Imported from field and pivot field is mandatory for imported field.')
        }
      }

      // 4. for list and multi list field, it should have list id or should be a dynamic list
      if ([FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate.data_structure)) {
        if (!fieldTemplate.list_id && !fieldTemplate.dynamic_field_type) {
          throw new Error('List must have list associated or must be a dynamic list.')
        }
      }

      const data = {
        field_template: fieldTemplate,
        prev_field_type: field.data_structure,
        process_template_id: atData.process_template_id,
      }
      const response = await fieldTemplateService.updateFieldTemplateOnce(user, data)

      let list_id = field.list_id
      if (![FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate.data_structure)) {
        list_id = null
      } else {
        list_id = fieldTemplate.list_id
      }

      let updatedFtData = ftData?.map((it) => {
        let subfield = `${response?.subfield?.id}`
        if (field.data_structure == fieldTemplate.data_structure) {
          subfield = field.subfield_template_ids
        }

        return it.id == field.id
          ? {
              ...it,
              ...fieldTemplate,
              subfield_template_ids: subfield,
              list_id,
            }
          : it
      })

      if (response?.subfield) {
        updatedFtData.push(response?.subfield)
      }

      if (
        field.data_structure == FIELD_TYPES.TABLE &&
        fieldTemplate.data_structure != FIELD_TYPES.TABLE
      ) {
        setFtData(
          updatedFtData?.filter((it) => !field?.subfield_template_ids?.includes(it.id + '')),
        )
      } else {
        setFtData(updatedFtData)
      }

      setIsEditDialogOpen(false)
      if (!doInBackground) {
        toast.success('Field template updated successfully.')
      }
      return true
    } catch (error) {
      toast.error(error.message)
      return false
    }
  }

  const updateFieldTemplate = async (fieldTemplate, doInBackground = false) => {
    if (doInBackground) {
      if (updateFieldRef.current) {
        clearTimeout(updateFieldRef.current)
      }
      updateFieldRef.current = setTimeout(async () => {
        await saveFieldTemplate(fieldTemplate, doInBackground)
      }, 2000)
    } else {
      return await saveFieldTemplate(fieldTemplate, doInBackground)
    }
  }

  const saveActivityDetails = async (activity) => {
    try {
      // check conditional requirement before saving
      // 1. delegable to teams for delegable activity
      if (activity.is_delegatable) {
        if (activity.delegation_team_ids.length <= 0) {
          throw new Error('Select at least one eligible team for delegation.')
        }
      }

      await activityTemplateService.saveActivityTemplate(user, activity)
      setAtData(activity)
      toast.success('Activity details updated successfully!')
    } catch (error) {
      toast.error(error.message)
    }
  }

  const handleDeleteActivity = async (id) => {
    try {
      await workflowService.deleteActivityTemplate(user, id)
      toast.success('Activity template deleted successfully!')
    } catch (error) {
      toast.error(error.message)
    }
  }

  if (loading) {
    return (
      <Box
        style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="primary" size={40} />
      </Box>
    )
  }

  const options = [
    { id: 'new-activity', title: 'Edit Activity', icon: <EditIcon fontSize="small" /> },
    { id: 'user-assignment', title: 'User Assignment', icon: <GroupsIcon fontSize="small" /> },
    // { id: 'set-rules', title: 'Set Rules', icon: <SettingsIcon fontSize="small" /> },
  ]

  const handleOptionSelect = (optionId) => {
    switch (optionId) {
      case 'new-activity':
        setIsEditActivityDialogOpen(true)
        break
      case 'user-assignment':
        navigate('/admin/user-assignments/' + activityTemplateId)
        break
      case 'set-rules':
        setOpenRuleSettingDialog(true)
        break
      default:
        window.alert('Not a valid option!')
        break
    }
  }

  const navigateTo = (id) => {
    navigate(`/admin/activity-config/${id}`)
    window?.location?.reload() // forcing reload as it's not loading data on just navigation without reload
  }

  const getFieldTitle = (field) => {
    return (
      <Typography>
        {field.data_structure == FIELD_TYPES.TABLE && (
          <IconButton
            style={{ ...iconButtonStyle, marginRight: 8 }}
            onClick={() => {
              setDialogFtId(field.id)
              setTabularFormDialogOpen(true)
            }}
            size="small"
          >
            <TableViewIcon style={{ fontSize: 18 }} color="primary" />
          </IconButton>
        )}
        <strong>
          {field.field_template_name}
          {field.is_required ? <span style={{ color: 'red' }}> * </span> : ' '}
        </strong>
        <span>
          {' '}
          ({field.data_structure}){' '}
          {field.is_derived_field ? (
            <Chip
              style={{ fontSize: 10, height: 18 }}
              variant="outlined"
              color="success"
              size="small"
              label="Derived"
            />
          ) : null}{' '}
          {field.is_imported_field ? (
            <Chip
              style={{ fontSize: 10, height: 18 }}
              variant="outlined"
              color="default"
              size="small"
              label="Imported"
            />
          ) : null}{' '}
          {field.is_computed ? (
            <Chip
              style={{ fontSize: 10, height: 18 }}
              variant="outlined"
              color="warning"
              size="small"
              label="Computed"
            />
          ) : null}
        </span>
      </Typography>
    )
  }

  const moveFieldTemplate = (fieldTemplates, movingFieldTemplateId, instruction) => {
    // Check for duplicates in the sequence
    const sequenceCounts = fieldTemplates.reduce((acc, ft) => {
      acc[ft.sequence] = (acc[ft.sequence] || 0) + 1
      return acc
    }, {})
    const hasDuplicates = Object.values(sequenceCounts).some((count) => count > 1)

    // Normalize the sequence to ensure there are no duplicates
    fieldTemplates = fieldTemplates
      .sort((a, b) => a.sequence - b.sequence)
      .map((ft, i) => ({ ...ft, sequence: i + 1 }))

    // Find the index of the item to move
    const index = fieldTemplates.findIndex((ft) => ft.id === movingFieldTemplateId)
    if (index === -1) return { fieldTemplates, hasDuplicates } // If the item is not found, return the array unchanged

    // Remove the item from its current position
    const [movingItem] = fieldTemplates.splice(index, 1)

    let newIndex
    switch (instruction) {
      case 'top':
        newIndex = 0
        break
      case 'bottom':
        newIndex = fieldTemplates.length
        break
      case 'up':
        newIndex = Math.max(index - 1, 0)
        break
      case 'down':
        newIndex = Math.min(index + 1, fieldTemplates.length)
        break
      default:
        // If the instruction is a specific number, use it as the target index
        newIndex = parseInt(instruction, 10) - 1
        if (isNaN(newIndex) || newIndex < 0 || newIndex > fieldTemplates.length) {
          return { fieldTemplates, hasDuplicates } // Invalid specific position
        }
    }

    // Insert the item at the new position
    fieldTemplates.splice(newIndex, 0, movingItem)

    // Update the sequence based on the new order
    return {
      fieldTemplates: fieldTemplates.map((ft, i) => ({ ...ft, sequence: i + 1 })),
      hasDuplicates,
    }
  }

  const moveField = async (fieldId, instruction) => {
    const oldFtData = [...ftData]

    try {
      const field = ftData.find((it) => it.id === fieldId)

      // get all fields inside page except subfields
      let fields = []
      if (field.is_subfield) {
        fields = ftData.filter((it) => it.table_field_template_id === field.table_field_template_id)
      } else {
        fields = ftData.filter(
          (it) => it.page_template_id === field.page_template_id && !it.is_subfield,
        )
      }

      // sequencing and removing duplicate sequence
      const sequenced = moveFieldTemplate(fields, fieldId, instruction)

      // create map for fast access
      const idSequenceMap = sequenced.fieldTemplates?.reduce((acc, cur) => {
        return { ...acc, [cur.id]: cur.sequence }
      }, {})

      // update the local state
      setFtData(
        ftData.map((it) => (idSequenceMap[it.id] ? { ...it, sequence: idSequenceMap[it.id] } : it)),
      )

      // call api to update databa
      await adminFieldService.updateFieldSequenceInBulk(user, sequenced.fieldTemplates)
    } catch (error) {
      setFtData(oldFtData)
    }
  }

  const movePageTemplate = (pageTemplates, movingPageTemplateId, instruction) => {
    // Check for duplicates in the sequence
    const sequenceCounts = pageTemplates.reduce((acc, ft) => {
      acc[ft.sequence] = (acc[ft.sequence] || 0) + 1
      return acc
    }, {})
    const hasDuplicates = Object.values(sequenceCounts).some((count) => count > 1)

    // Normalize the sequence to ensure there are no duplicates
    pageTemplates = pageTemplates
      .sort((a, b) => a.sequence - b.sequence)
      .map((ft, i) => ({ ...ft, sequence: i + 1 }))

    // Find the index of the item to move
    const index = pageTemplates.findIndex((ft) => ft.id === movingPageTemplateId)
    if (index === -1) return { fieldTemplates: pageTemplates, hasDuplicates } // If the item is not found, return the array unchanged

    // Remove the item from its current position
    const [movingItem] = pageTemplates.splice(index, 1)

    let newIndex
    switch (instruction) {
      case 'top':
        newIndex = 0
        break
      case 'bottom':
        newIndex = pageTemplates.length
        break
      case 'up':
        newIndex = Math.max(index - 1, 0)
        break
      case 'down':
        newIndex = Math.min(index + 1, pageTemplates.length)
        break
      default:
        // If the instruction is a specific number, use it as the target index
        newIndex = parseInt(instruction, 10) - 1
        if (isNaN(newIndex) || newIndex < 0 || newIndex > pageTemplates.length) {
          return { pageTemplates: pageTemplates, hasDuplicates } // Invalid specific position
        }
    }

    // Insert the item at the new position
    pageTemplates.splice(newIndex, 0, movingItem)

    // Update the sequence based on the new order
    return {
      pageTemplates: pageTemplates.map((ft, i) => ({ ...ft, sequence: i + 1 })),
      hasDuplicates,
    }
  }

  const movePage = async (pageId, instruction) => {
    const oldPages = [...pages]

    try {
      // sequencing and removing duplicate sequence
      const sequenced = movePageTemplate(pages, pageId, instruction)

      // update the local state
      setPages(sequenced.pageTemplates)

      // call api to update database
      await adminPageService.updatePageSequenceBulk(user, sequenced.pageTemplates)
    } catch (error) {
      setPages(oldPages)
    }
  }

  return (
    <Box style={{ position: 'relative' }} className="activity-config">
      <Box
        style={{
          width: '100%',
          backgroundColor: '#fafafa',
          position: 'sticky',
          top: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0px 1px 5px #e0e0e0',
          zIndex: 100,
          marginBottom: '10px',
        }}
      >
        <Stepper
          sx={{ overflow: 'auto' }}
          nonLinear
          activeStep={allActivities.findIndex((it) => it.id == activityTemplateId)}
        >
          {allActivities.map(({ activity_template_name, id }, index) => (
            <Step key={id} color="primary">
              <StepLabel
                StepIconProps={{
                  sx: {
                    '& circle': {
                      fill: activityTemplateId == id ? 'primary' : 'gray',
                    },
                    '& text': {
                      fill: 'white',
                    },
                  },
                }}
              >
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    onClick={() => (activityTemplateId != id ? navigateTo(id) : null)}
                    variant="p"
                    style={{
                      fontWeight: activityTemplateId == id ? '600' : '400',
                      cursor: 'pointer',
                    }}
                  >
                    {activity_template_name}
                  </Typography>
                  {activityTemplateId == id ? (
                    <OptionMenu
                      options={options}
                      onOptionSelect={handleOptionSelect}
                      tooltipContent="More actions"
                    />
                  ) : null}
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {[ACTIVITY_TYPES.DATA_ENTRY, ACTIVITY_TYPES.LONG_RUNNING].includes(atData?.activity_type) ? (
        <Box>
          {pages?.map((page) => {
            return (
              <fieldset
                style={{
                  borderRadius: '20px',
                  marginBottom: '10px',
                }}
                key={page.id}
              >
                <legend
                  style={{
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    top: '-20px',
                  }}
                >
                  <Typography style={{ marginRight: 8 }}>{page?.page_template_name}</Typography>{' '}
                  <MovingFieldPopOver
                    moveTemplate={movePage}
                    templateId={page.id}
                    templateType={'page'}
                    currentSequence={page.sequence}
                  />
                  <Tooltip title="Page Setting">
                    <IconButton
                      style={{ ...iconButtonStyle, marginLeft: 8 }}
                      name={`p_${page.id}`}
                      onClick={() => {
                        setDialogPtId(page.id)
                        setEditingPageName(page?.page_template_name)
                        setIsEditPageDialogOpen(true)
                      }}
                      size="small"
                    >
                      <SettingsIcon style={{ fontSize: 18 }} color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Grid Layout View">
                    <IconButton
                      style={{ ...iconButtonStyle, marginLeft: 8 }}
                      color="primary"
                      onClick={() => {
                        setGridLayoutOpen(true)
                        setGridLayoutPageTemplateId(page.id)
                      }}
                    >
                      <ViewQuiltIcon style={{ fontSize: 18 }} color="primary" />
                    </IconButton>
                  </Tooltip>
                </legend>

                {ftData
                  ?.filter((ft) => ft.page_template_id === page.id && !ft.is_subfield)
                  ?.sort((a, b) => a.sequence - b.sequence)
                  ?.map((field) => {
                    let subfieldIds = []
                    if (field.data_structure === FIELD_TYPES.TABLE) {
                      if (ftData.find((ft) => ft.id == field.id).subfield_template_ids) {
                        subfieldIds = ftData
                          .find((ft) => ft.id == field.id)
                          .subfield_template_ids.split(',')
                          .map((elem) => Number(elem))
                      }
                    }

                    const sftData = ftData.filter((ft) => subfieldIds.includes(ft.id))

                    return (
                      <Box key={field.id}>
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <MovingFieldPopOver
                            moveTemplate={moveField}
                            templateId={field.id}
                            templateType={'field'}
                            currentSequence={field.sequence}
                          />
                          <IconButton
                            style={iconButtonStyle}
                            onClick={() => {
                              setDialogFtId(field.id)
                              setIsEditDialogOpen(true)
                            }}
                            size="small"
                          >
                            <SettingsIcon style={{ fontSize: 18 }} color="primary" />
                          </IconButton>
                          {field.data_structure !== FIELD_TYPES.TABLE && (
                            <IconButton
                              style={iconButtonStyle}
                              onClick={() => setShowDeleteField(field)}
                              size="small"
                            >
                              <TrashIcon style={{ fontSize: 18 }} color="primary" />
                            </IconButton>
                          )}
                          <Tooltip
                            title={
                              <Box>
                                <Typography
                                  style={{ cursor: 'copy' }}
                                  onClick={() => {
                                    toast.success('Field Id copied to clipboard!')
                                    copyToClipboard(field.id)
                                  }}
                                >
                                  {field.id}
                                </Typography>
                              </Box>
                            }
                          >
                            <Typography variant="body1">{getFieldTitle(field)}</Typography>
                          </Tooltip>
                        </Box>

                        <br />
                        {field.data_structure == FIELD_TYPES.TABLE &&
                          sftData
                            .sort((a, b) => a.sequence - b.sequence)
                            .map((sft) => (
                              <Box key={sft.id} id={sft.id}>
                                <Box
                                  style={{
                                    display: 'flex',
                                    paddingLeft: '6rem',
                                    alignItems: 'center',
                                    gap: 8,
                                  }}
                                >
                                  <MovingFieldPopOver
                                    templateId={sft.id}
                                    moveTemplate={moveField}
                                    currentSequence={sft.sequence}
                                    templateType={'subfield'}
                                  />
                                  <IconButton
                                    style={iconButtonStyle}
                                    name={`f_${sft.id}`}
                                    onClick={() => {
                                      setDialogFtId(sft.id)
                                      setIsEditDialogOpen(true)
                                    }}
                                    size="small"
                                  >
                                    <SettingsIcon style={{ fontSize: 18 }} color="primary" />
                                  </IconButton>
                                  <IconButton
                                    style={iconButtonStyle}
                                    onClick={() => setShowDeleteField(sft)}
                                    size="small"
                                  >
                                    <TrashIcon style={{ fontSize: 18 }} color="primary" />
                                  </IconButton>
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography
                                          style={{ cursor: 'copy' }}
                                          onClick={() => {
                                            toast.success('Field Id copied to clipboard!')
                                            copyToClipboard(sft.id)
                                          }}
                                        >
                                          {sft.id}
                                        </Typography>
                                      </Box>
                                    }
                                  >
                                    <Typography>{getFieldTitle(sft)}</Typography>
                                  </Tooltip>
                                </Box>
                                <br />
                              </Box>
                            ))}
                        {field.data_structure == FIELD_TYPES.TABLE && (
                          <>
                            <Box style={{ display: 'flex', paddingLeft: '6rem' }}>
                              <Tooltip
                                title={`Add Subfield to Table ("${field.field_template_name}")`}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="primary"
                                  onClick={() => handleAddSubfield(field.id, page.id)}
                                >
                                  Add Subfield
                                </Button>
                              </Tooltip>
                            </Box>
                            <br />
                          </>
                        )}
                      </Box>
                    )
                  })}
                <Box style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <Tooltip title={`Add Field to Page ("${page.page_template_name}")`}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => handleAddField(page.id)}
                    >
                      Add Field
                    </Button>
                  </Tooltip>
                  <Tooltip title={`Add Derived Field to Page ("${page.page_template_name}")`}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => setDerivePage({ open: true, pageId: page.id })}
                    >
                      Derive Field
                    </Button>
                  </Tooltip>
                  <Tooltip title={`Add Import Field to Page ("${page.page_template_name}")`}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => setImportField({ open: true, pageId: page.id })}
                    >
                      Import Field
                    </Button>
                  </Tooltip>
                </Box>
              </fieldset>
            )
          })}
        </Box>
      ) : (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '90vh',
          }}
        >
          It's a {atData?.activity_type} activity. We don't need to setup pages and fields!
        </Box>
      )}

      {/* Edit Field Properties Dialog */}
      {isEditDialogOpen && (
        <EditFieldTemplateDialog
          initialFieldTemplate={ftData.find((it) => it.id == dialogFtId)}
          importedFieldList={importedFieldList}
          lists={lists}
          tfData={tfData}
          ftData={ftData}
          setDialogOpen={setIsEditDialogOpen}
          processTemplateId={atData.process_template_id}
          activityTemplateId={activityTemplateId}
          activityTemplate={atData}
          onChangeSave={(fieldTemplate) => updateFieldTemplate(fieldTemplate, false)}
          onFieldDelete={(fieldTemplateId) => {}}
          allTeamTypes={allTeamTypes}
          allTeamSubTypes={allTeamSubTypes}
        />
      )}

      {/* Edit Page Properties Dialog */}
      {isEditPageDialogOpen && (
        <>
          <DialogWrapper
            heading={
              ftData.find((ft) => ft.page_template_id === dialogPtId).page_template_name +
              ' (Page Settings)'
            }
            open={isEditPageDialogOpen}
            onClose={() => setIsEditPageDialogOpen(false)}
          >
            <TextField
              type="text"
              value={editingPageName}
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              onChange={(e) => setEditingPageName(e.target.value)}
              label={`Page Template Name`}
            />
            <Box
              style={{
                display: 'flex',
                columnGap: 16,
                marginTop: 16,
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={() => setIsEditPageDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={() => handleUpdatePageTemplateDetails(dialogPtId, editingPageName)}
              >
                Save
              </Button>
            </Box>
          </DialogWrapper>
        </>
      )}
      {isEditActivityDialogOpen && (
        <UpdateActivityDetails
          ftData={importedFieldList.filter(
            (it) => it.process_template_id === atData.process_template_id,
          )}
          setDialogOpen={(status) => setIsEditActivityDialogOpen(status)}
          activityTemplate={atData}
          saveActivityDetails={saveActivityDetails}
          deleteActivity={(activityTemplate) => setDeleteActivity(activityTemplate)}
        />
      )}
      {atData && atData.activity_type != ACTIVITY_TYPES.REVIEW && (
        <Box style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <Tooltip
            title={
              atData ? `Add Page to Task ("${atData.activity_template_name}")` : 'Add Page to Task'
            }
          >
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => handleAddPage(activityTemplateId)}
            >
              Add Page
            </Button>
          </Tooltip>
          <Tooltip
            title={
              atData
                ? `Derive Page to Task ("${atData.activity_template_name}")`
                : 'Add Page to Task'
            }
          >
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => setDerivePage({ open: true, pageId: null })}
            >
              Derive Page
            </Button>
          </Tooltip>
        </Box>
      )}
      {showDeleteField ? (
        <ConfirmationBox
          setIsDialogOpen={(v) => {
            v == false && setShowDeleteField(null)
          }}
          isDialogOpen={!!showDeleteField}
          title={`Want to delete field ${showDeleteField.field_template_name}?`}
          onYes={() => {
            handleDeleteField(showDeleteField.id)
            setShowDeleteField(null)
          }}
        />
      ) : null}
      {showDeletePage ? (
        <ConfirmationBox
          setIsDialogOpen={(v) => {
            v == false && setShowDeletePage(null)
          }}
          isDialogOpen={!!showDeletePage}
          title={`Want to delete page ${showDeletePage.page_template_name}?`}
          onYes={() => {
            handleDeletePage(showDeletePage.id)
            setShowDeletePage(null)
          }}
        />
      ) : null}
      {deleteActivity ? (
        <ConfirmationBox
          setIsDialogOpen={(v) => {
            v == false && setDeleteActivity(null)
          }}
          isDialogOpen={!!deleteActivity}
          title={`Want to delete activity ${deleteActivity.activity_template_name}?`}
          onYes={() => {
            handleDeleteActivity(deleteActivity.id)
            setDeleteActivity(null)
          }}
        />
      ) : null}
      {derivePage ? (
        <DerivePageDialog
          open={derivePage.open}
          setOpen={() => setDerivePage({ pageId: null, open: false })}
          pageId={derivePage.pageId}
          activityTemplateId={activityTemplateId}
          processTemplateId={atData?.process_template_id}
          onDeriveSuccess={getDataFromServer}
        />
      ) : null}
      {importField ? (
        <ImportPageDialog
          open={importField.open}
          ftData={ftData}
          importedFieldList={importedFieldList}
          setOpen={() => setImportField({ pageId: null, open: false })}
          pageId={importField.pageId}
          activityTemplateId={activityTemplateId}
          processTemplateId={atData?.process_template_id}
          onDeriveSuccess={getDataFromServer}
        />
      ) : null}
      {tabularFormDialogOpen ? (
        <TabularFormDialog
          fieldTemplate={ftData.find((it) => it.id == dialogFtId)}
          ftData={ftData}
          onClose={() => setTabularFormDialogOpen(false)}
          importedFieldList={importedFieldList}
          lists={lists}
          tfData={tfData}
          atData={atData}
          setDialogOpen={setIsEditDialogOpen}
          processTemplateId={atData.process_template_id}
          activityTemplateId={activityTemplateId}
          activityTemplate={atData}
          updateFieldTemplate={(fieldTemplate, doInBackground) =>
            updateFieldTemplate(fieldTemplate, doInBackground)
          }
          handleAddSubfield={(tableFieldId, pageId) => handleAddSubfield(tableFieldId, pageId)}
        />
      ) : null}
      {gridLayoutOpen ? (
        <GridLayoutView
          fields={ftData
            ?.filter((ft) => ft.page_template_id === gridLayoutPageTemplateId && !ft.is_subfield)
            ?.sort((a, b) => a.sequence - b.sequence)}
          onClose={() => {
            setGridLayoutOpen(false)
            setGridLayoutPageTemplateId(null)
          }}
          allFields={ftData}
          updateFields={setFtData}
        />
      ) : null}
      {openRuleSettingDialog && (
        <RuleEditorDialog
          activityTemplateId={activityTemplateId}
          processTemplateId={atData.process_template_id}
          open={openRuleSettingDialog}
          onClose={() => setOpenRuleSettingDialog(false)}
        />
      )}
    </Box>
  )
}

const MovingFieldPopOver = ({ templateId, moveTemplate, currentSequence, templateType }) => {
  const [sequencePopOverId, setSequencePopOverId] = useState(undefined)
  const [sequencePopOverOpen, setSequencePopOverOpen] = useState(false)
  const [sequencePopOverAnchorEl, setSequencePopOverAnchorEl] = useState(null)
  const [changedSequence, setChangedSequence] = useState(0)
  const classes = useStyles()

  return (
    <>
      <IconButton
        aria-describedby={sequencePopOverId}
        style={iconButtonStyle}
        onClick={(e) => {
          setSequencePopOverOpen(true)
          setSequencePopOverId(`pop-${templateType}-${templateId}`)
          setSequencePopOverAnchorEl(e.currentTarget)
        }}
        size="small"
      >
        <ImportExportIcon style={{ fontSize: 18 }} color="primary" />
      </IconButton>

      {sequencePopOverOpen && (
        <Popover
          key={templateId}
          id={sequencePopOverId}
          open={sequencePopOverOpen}
          onClose={async () => {
            setSequencePopOverAnchorEl(null)
            setSequencePopOverId(undefined)
            setSequencePopOverOpen(false)

            if (changedSequence && changedSequence !== currentSequence) {
              await moveTemplate(templateId, changedSequence)
            }
            setChangedSequence(null)
          }}
          anchorEl={sequencePopOverAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          className={classes.popover}
          classes={{
            paper: classes.paper,
          }}
        >
          <Box>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: 8,
                cursor: 'pointer',
              }}
              onClick={async () => {
                await moveTemplate(templateId, MOVING_INSTRUCTIONS_STATIC.TOP)
                setSequencePopOverAnchorEl(null)
                setSequencePopOverId(undefined)
                setSequencePopOverOpen(false)
              }}
            >
              <Typography>Move to Top</Typography>{' '}
            </Box>
            <Divider />
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: 8,
                cursor: 'pointer',
              }}
              onClick={async () => {
                await moveTemplate(templateId, MOVING_INSTRUCTIONS_STATIC.UP)
                setSequencePopOverAnchorEl(null)
                setSequencePopOverId(undefined)
                setSequencePopOverOpen(false)
              }}
            >
              <Typography>Move Up</Typography>{' '}
            </Box>
            <Divider />
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: 8,
              }}
            >
              <Typography>Move to </Typography>{' '}
              <TextField
                size="small"
                placeholder="Enter Sequence"
                type="number"
                value={changedSequence}
                onChange={(e) => setChangedSequence(e.target.value)}
                style={{ width: 60 }}
              />
            </Box>
            <Divider />
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: 8,
                cursor: 'pointer',
              }}
              onClick={async () => {
                await moveTemplate(templateId, MOVING_INSTRUCTIONS_STATIC.DOWN)
                setSequencePopOverAnchorEl(null)
                setSequencePopOverId(undefined)
                setSequencePopOverOpen(false)
              }}
            >
              <Typography>Move Down</Typography>{' '}
            </Box>
            <Divider />
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: 8,
                cursor: 'pointer',
              }}
              onClick={async () => {
                await moveTemplate(templateId, MOVING_INSTRUCTIONS_STATIC.BOTTOM)
                setSequencePopOverAnchorEl(null)
                setSequencePopOverId(undefined)
                setSequencePopOverOpen(false)
              }}
            >
              <Typography>Move to Bottom</Typography>{' '}
            </Box>
          </Box>
        </Popover>
      )}
    </>
  )
}
